import { useContext, useEffect, useState } from "react";

import Spinner from "../../../../shared/components/spinner/spinner";
import AuthContext from "../../../../shared/context/auth-context";
import {IEnumerationOption} from "../../../../shared/definitions/datatypes";
import useHttp from "../../../../shared/hooks/use-http";
import { getAllData } from "../../../../shared/utils/api";
import {IObject, validateLoadingStatusNew} from "../../../../shared/utils/utils";
import SearchInput from "../../../components/search-input/search-input";

export interface IAlertCaseSetupData {
    domain_id: string;
    domain_name: string;
    process_type_id: string;
    process_type_name: string;
    consolidate_on_entity_id: string;
    event_capture_id: string;
}

interface IAlertCaseSetupConfig {
    alertcaseData: IAlertCaseSetupData;
    onDataChange: (newData: IAlertCaseSetupData) => void;
}

const AlertCaseSetup = ({alertcaseData, onDataChange}: IAlertCaseSetupConfig) => {
  const emptyAlertCaseData: IAlertCaseSetupData = {
    domain_id: "", 
    process_type_id: "",
    process_type_name: "",
    domain_name: "",
    consolidate_on_entity_id: "",
    event_capture_id: ""
  };

  const authCtx = useContext(AuthContext);
  const [domainTypes, setDomainTypes] = useState<IEnumerationOption[]>([]);
  const [processTypes, setProcessTypes] = useState<IObject[]>([]);

  const {
    sendRequest: getDomainTypes,
    status: getDomainTypesStatus,
    data: getDomainTypesData,
    error: getDomainTypesError
  } = useHttp(getAllData, true, authCtx.token, undefined, "manager");

  const {
    sendRequest: getProcessTypes,
    status: getProcessTypesStatus,
    data: getProcessTypesData,
    error: getProcessTypesError
  } = useHttp(getAllData, false, authCtx.token, undefined, "manager");

  useEffect(() => {
    getDomainTypes("domain_types?active=true&order_by=name asc");
    if (alertcaseData.domain_id) {
      getProcessTypes(`domain_type/${alertcaseData.domain_id}/process_types?active=true&order_by=name asc`);
    }
  }, []);

  useEffect(() => {
    if (getDomainTypesStatus === "completed" && !getDomainTypesError && getDomainTypesData){
      setDomainTypes(getDomainTypesData.rows.map((dt: IObject) => ({
        id: dt.id,
        name: dt.name
      })));
    }
  }, [getDomainTypesStatus, getDomainTypesError, getDomainTypesData]);

  useEffect(() => {
    if (getProcessTypesStatus === "completed" && !getProcessTypesError && getProcessTypesData) {
      setProcessTypes(getProcessTypesData.rows);
    }
  }, [getProcessTypesStatus, getProcessTypesError, getProcessTypesData]);

  const handleOnDomainTypeSelect = (selectedDomainType: IEnumerationOption | undefined) => {
    const data = {...emptyAlertCaseData};
    if (selectedDomainType) {
      if (alertcaseData.domain_id !== selectedDomainType.id) {
        getProcessTypes(`domain_type/${selectedDomainType.id}/process_types?active=true&order_by=name asc`);
      }
      data.domain_name = selectedDomainType.name;
      data.domain_id = selectedDomainType.id;
    }
    onDataChange(data);
  };

  const handleOnProcessTypeSelect = (selectedProcessType: IEnumerationOption | undefined) => {
    const data = {
      ...emptyAlertCaseData,
      domain_id: alertcaseData.domain_id,
      domain_name: alertcaseData.domain_name
    };

    if (selectedProcessType) {
      data.process_type_id = selectedProcessType.id;
      data.process_type_name = selectedProcessType.name;
      const processType = processTypes.find(pt => pt.id === selectedProcessType.id);
      data.consolidate_on_entity_id = processType?.consolidate_on_entity_id || "";
      data.event_capture_id = processType?.event_capture_screen_id || "" ;
    }
    onDataChange(data);
  };

  return <div>
    {validateLoadingStatusNew([getDomainTypesStatus, getProcessTypesStatus]) && <Spinner />}
    <div className="domainDropdown">
      <SearchInput
        label={"Domain Type"}
        handleOnChange={handleOnDomainTypeSelect}
        data={domainTypes}
        selectedOption={alertcaseData.domain_id}
        inputKey={"domain"}
        required={true}
      />
    </div>
    {alertcaseData.domain_id &&
      <div className="processTypeDropdown">
        <SearchInput
          label={"Process Type"}
          handleOnChange={handleOnProcessTypeSelect}
          data={processTypes.map((pt) => ({
            id: pt.id,
            name: pt.name
          }))}
          selectedOption={alertcaseData.process_type_id}
          inputKey="process_type"
          required={true}
        />
      </div>
    }
  </div>;
};

export default AlertCaseSetup;